import { apiService } from "@/services";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { handleApiError } from "@/utils";
import queryClient from "@/query-client";
import {
  RankTrackerHeroProduct,
  RankTrackerKeyword,
  BrandSummaryResponse,
  AsinCorrelationResponse,
  AddRankTrackerKeywordsParams,
  BulkAddRankTrackerKeywordsParams,
  RankTrackerReport,
  DailyRanksResponse,
} from "@/types";
import type { AdPushCutoffsRequest, AdPushCutoffsResponse } from "@/types/ad-push";
import { DEFAULT_STALE_TIME } from "@/constants";
import { toast } from "sonner";

let optimisticKeywordIdCounter = -1;

const SNAPSHOT_REFETCH_DELAYS_MS = [15_000, 45_000];

const scheduleSnapshotRefetch = (brandId: number) => {
  for (const delay of SNAPSHOT_REFETCH_DELAYS_MS) {
    window.setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: ["rank-tracker", "hero-products", brandId] });
      queryClient.invalidateQueries({ queryKey: ["rank-tracker", "keywords"], refetchType: "none" });
      queryClient.invalidateQueries({ queryKey: ["rank-tracker", "brand-summary", brandId] });
    }, delay);
  }
};

type AddKeywordsMutationContext = {
  heroProductsSnapshot: Array<[readonly unknown[], RankTrackerHeroProduct[] | undefined]>;
  keywordsSnapshot: Array<[readonly unknown[], RankTrackerKeyword[] | undefined]>;
};

type DeleteKeywordMutationContext = {
  heroProductsSnapshot: Array<[readonly unknown[], RankTrackerHeroProduct[] | undefined]>;
  keywordsSnapshot: Array<[readonly unknown[], RankTrackerKeyword[] | undefined]>;
};

const applyAddKeywordsOptimisticPatch = async (
  variables: { brandId: number; heroAsin: string; keywords: string[]; source: string },
): Promise<AddKeywordsMutationContext> => {
  const heroProductsKey = ["rank-tracker", "hero-products", variables.brandId];
  await queryClient.cancelQueries({ queryKey: heroProductsKey });

  const heroProductsSnapshot = queryClient.getQueriesData<RankTrackerHeroProduct[]>({
    queryKey: heroProductsKey,
  });

  await queryClient.cancelQueries({ queryKey: ["rank-tracker", "keywords"] });
  const keywordsSnapshot = queryClient.getQueriesData<RankTrackerKeyword[]>({
    queryKey: ["rank-tracker", "keywords"],
  });

  const normalized = Array.from(
    new Set(
      variables.keywords
        .map((k) => k.trim().toLowerCase())
        .filter((k) => k.length > 0),
    ),
  );

  for (const [key, prev] of keywordsSnapshot) {
    if (!prev) continue;
    const existing = new Set(prev.map((k) => k.keyword.toLowerCase()));
    const toAdd = normalized
      .filter((kw) => !existing.has(kw))?.
      map<RankTrackerKeyword>((kw) => ({
        id: optimisticKeywordIdCounter--,
        keyword: kw,
        heroAsin: variables.heroAsin,
        currentRank: null,
        previousRank: null,
        searchVolume: null,
        bestRank: null,
        source: variables.source,
        dateAdded: new Date().toISOString(),
        rankHistory: [],
      }));
    if (toAdd.length > 0) {
      queryClient.setQueryData<RankTrackerKeyword[]>(key, [...prev, ...toAdd]);
    }
  }

  for (const [key, prev] of heroProductsSnapshot) {
    if (!prev) continue;
    const matchIndex = prev.findIndex((p) => p.asin === variables.heroAsin);
    if (matchIndex === -1) continue;

    const existingKeywordsForHero = new Set<string>();
    for (const [, keywordRows] of keywordsSnapshot) {
      if (!keywordRows) continue;
      for (const row of keywordRows) {
        if (row.heroAsin === variables.heroAsin) {
          existingKeywordsForHero.add(row.keyword.toLowerCase());
        }
      }
    }
    const newCount = normalized.filter((kw) => !existingKeywordsForHero.has(kw)).length;
    if (newCount === 0) continue;

    const next = prev.map((p, idx) =>
      idx === matchIndex ? { ...p, keywordCount: p.keywordCount + newCount } : p,
    );
    queryClient.setQueryData<RankTrackerHeroProduct[]>(key, next);
  }

  return { heroProductsSnapshot, keywordsSnapshot };
};

const rollbackSnapshots = (context: AddKeywordsMutationContext | DeleteKeywordMutationContext) => {
  for (const [key, snapshot] of context.heroProductsSnapshot) {
    queryClient.setQueryData(key, snapshot);
  }
  for (const [key, snapshot] of context.keywordsSnapshot) {
    queryClient.setQueryData(key, snapshot);
  }
};

export const useGetRankTrackerHeroProducts = (brandId?: number | null) => {
  return useQuery({
    queryKey: ["rank-tracker", "hero-products", brandId],
    queryFn: () =>
      apiService.get<RankTrackerHeroProduct[], { brand_id: number }>(
        `/rank-tracker/hero-products`,
        { brand_id: brandId! },
      ),
    enabled: !!brandId,
    placeholderData: keepPreviousData,
    staleTime: DEFAULT_STALE_TIME,
  });
};

export const useGetRankTrackerKeywords = (
  heroProductId?: number | null,
  brandId?: number | null,
  dateFrom?: string,
  dateTo?: string,
) => {
  return useQuery({
    queryKey: ["rank-tracker", "keywords", heroProductId, brandId, dateFrom, dateTo],
    queryFn: () =>
      apiService.get<RankTrackerKeyword[], { brand_id: number; date_from?: string; date_to?: string }>(
        `/rank-tracker/hero-products/${heroProductId}/keywords`,
        { brand_id: brandId!, date_from: dateFrom, date_to: dateTo },
      ),
    enabled: !!heroProductId && !!brandId,
    placeholderData: keepPreviousData,
    staleTime: DEFAULT_STALE_TIME,
  });
};

/**
 * Prefix for GET /rank-tracker/brands/{brandId}/daily-ranks — every window for one brand.
 * `useDeleteRankTrackerKeyword` invalidates this prefix (not the full key below) so a bulk
 * "Stop tracking" refetches the grid regardless of which window is on screen.
 */
export const getDailyRanksQueryKeyPrefix = (brandId?: number | null) =>
  ["rank-tracker", "daily-ranks", brandId] as const;

export const getDailyRanksQueryKey = (
  brandId?: number | null,
  dateFrom?: string,
  dateTo?: string,
) => [...getDailyRanksQueryKeyPrefix(brandId), dateFrom, dateTo] as const;

/**
 * GET /api/rank-tracker/brands/{brandId}/daily-ranks — `components/rank-tracker.md
 * § Target state — the daily ranks read`. `brandId` is a path segment, never `brand_id` in the
 * query. No `placeholderData` / `keepPreviousData`: a brand switch or a window change must show
 * the loading state rather than the previous brand's ranks.
 *
 * `enabled` (WEB-010, design-conformance pass item 7): additive and backwards-compatible -- every
 * existing caller keeps passing only `brandId`/`dateFrom`/`dateTo` and keeps working unchanged.
 * `RankTracker.tsx` uses it for a second call over the full 90-day window, gated to only run while
 * the keyword detail modal is open (`ANDed` with the existing `!!brandId` check, never replacing it).
 */
export const useGetDailyRanks = (
  brandId?: number | null,
  dateFrom?: string,
  dateTo?: string,
  enabled = true,
) => {
  return useQuery({
    queryKey: getDailyRanksQueryKey(brandId, dateFrom, dateTo),
    queryFn: () =>
      apiService.get<DailyRanksResponse, { date_from?: string; date_to?: string }>(
        `/rank-tracker/brands/${brandId}/daily-ranks`,
        { date_from: dateFrom, date_to: dateTo },
      ),
    enabled: !!brandId && enabled,
    staleTime: DEFAULT_STALE_TIME,
  });
};

export const useAddRankTrackerKeywords = () => {
  return useMutation<
    { inserted: number; skipped: number },
    unknown,
    AddRankTrackerKeywordsParams,
    AddKeywordsMutationContext
  >({
    mutationFn: (params) =>
      apiService.post<
        { inserted: number; skipped: number },
        { brand_id: number; hero_asin: string; keywords: string[]; source: string; keyword_ids?: number[] }
      >(`/rank-tracker/keywords`, {
        brand_id: params.brandId,
        hero_asin: params.heroAsin,
        keywords: params.keywords,
        source: params.source,
        // Sent when the caller acted on specific rows, so the backend updates
        // those rather than whichever duplicate matches the phrase.
        ...(params.keywordIds?.length ? { keyword_ids: params.keywordIds } : {}),
      }),
    onMutate: applyAddKeywordsOptimisticPatch,
    onError: (error, _variables, context) => {
      if (context) rollbackSnapshots(context);
      handleApiError(error);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["rank-tracker", "hero-products", variables.brandId],
      });
      queryClient.invalidateQueries({ queryKey: ["rank-tracker", "keywords"] });
      queryClient.invalidateQueries({
        queryKey: ["rank-tracker", "brand-summary", variables.brandId],
      });
      scheduleSnapshotRefetch(variables.brandId);
    },
  });
};

export const useBulkAddRankTrackerKeywords = () => {
  return useMutation<
    { inserted: number; skipped: number },
    unknown,
    BulkAddRankTrackerKeywordsParams,
    AddKeywordsMutationContext
  >({
    mutationFn: (params) =>
      apiService.post<
        { inserted: number; skipped: number },
        { brand_id: number; items: Array<{ hero_asin: string; keyword: string; source: string }> }
      >(`/rank-tracker/keywords/bulk`, {
        brand_id: params.brandId,
        items: params.keywords?.map((keyword) => ({
          hero_asin: params.heroAsin,
          keyword,
          source: params.source,
        })),
      }),
    onMutate: applyAddKeywordsOptimisticPatch,
    onError: (error, _variables, context) => {
      if (context) rollbackSnapshots(context);
      handleApiError(error);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["rank-tracker", "hero-products", variables.brandId],
      });
      queryClient.invalidateQueries({ queryKey: ["rank-tracker", "keywords"] });
      queryClient.invalidateQueries({
        queryKey: ["rank-tracker", "brand-summary", variables.brandId],
      });
      scheduleSnapshotRefetch(variables.brandId);
    },
  });
};

export const useDeleteRankTrackerKeyword = () => {
  return useMutation<
    void,
    unknown,
    { id: number; brandId: number },
    DeleteKeywordMutationContext
  >({
    mutationFn: ({ id, brandId }) =>
      apiService.delete<void, unknown>(`/rank-tracker/keywords/${id}`, {}, { params: { brand_id: brandId } }),
    onMutate: async (variables) => {
      const keywordsSnapshot = queryClient.getQueriesData<RankTrackerKeyword[]>({
        queryKey: ["rank-tracker", "keywords"],
      });

      let deletedHeroAsin: string | null = null;
      for (const [, prev] of keywordsSnapshot) {
        if (!prev) continue;
        const match = prev.find((k) => k.id === variables.id);
        if (match) {
          deletedHeroAsin = match.heroAsin;
          break;
        }
      }

      await queryClient.cancelQueries({ queryKey: ["rank-tracker", "keywords"] });

      const heroProductsKey = ["rank-tracker", "hero-products", variables.brandId];
      await queryClient.cancelQueries({ queryKey: heroProductsKey });
      const heroProductsSnapshot = queryClient.getQueriesData<RankTrackerHeroProduct[]>({
        queryKey: heroProductsKey,
      });

      for (const [key, prev] of keywordsSnapshot) {
        if (!prev) continue;
        const next = prev.filter((k) => k.id !== variables.id);
        if (next.length !== prev.length) {
          queryClient.setQueryData<RankTrackerKeyword[]>(key, next);
        }
      }

      if (deletedHeroAsin) {
        for (const [key, prev] of heroProductsSnapshot) {
          if (!prev) continue;
          const next = prev.map((p) =>
            p.asin === deletedHeroAsin
              ? { ...p, keywordCount: Math.max(0, p.keywordCount - 1) }
              : p,
          );
          queryClient.setQueryData<RankTrackerHeroProduct[]>(key, next);
        }
      }

      return { heroProductsSnapshot, keywordsSnapshot };
    },
    onError: (error, _variables, context) => {
      if (context) rollbackSnapshots(context);
      handleApiError(error);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["rank-tracker", "hero-products", variables.brandId],
      });
      queryClient.invalidateQueries({ queryKey: ["rank-tracker", "keywords"] });
      queryClient.invalidateQueries({
        queryKey: ["rank-tracker", "brand-summary", variables.brandId],
      });
      queryClient.invalidateQueries({
        queryKey: getDailyRanksQueryKeyPrefix(variables.brandId),
      });
      queryClient.invalidateQueries({ queryKey: ["product", "keywords"] });
      queryClient.invalidateQueries({ queryKey: ["product", "detail"] });
    },
  });
};

export const useGetAsinCorrelation = (
  heroProductId?: number | null,
  brandId?: number | null,
  enabled = true,
) => {
  return useQuery({
    queryKey: ["rank-tracker", "asin-correlation", heroProductId, brandId],
    queryFn: () =>
      apiService.get<
        AsinCorrelationResponse,
        { brand_id: number }
      >(
        `/rank-tracker/hero-products/${heroProductId}/correlation`,
        { brand_id: brandId! },
      ),
    enabled: !!heroProductId && !!brandId && enabled,
    placeholderData: keepPreviousData,
    staleTime: DEFAULT_STALE_TIME,
  });
};

export const useGetBrandSummary = (
  brandId?: number | null,
  dateFrom?: string,
  dateTo?: string,
  asinIds?: number[],
) => {
  return useQuery({
    queryKey: ["rank-tracker", "brand-summary", brandId, dateFrom, dateTo, asinIds && asinIds.length > 0 ? asinIds : undefined],
    queryFn: () =>
      apiService.get<
        BrandSummaryResponse,
        { brand_id: number; date_from?: string; date_to?: string; asin_ids?: number[] }
      >(`/rank-tracker/brand-summary`, {
        brand_id: brandId!,
        date_from: dateFrom,
        date_to: dateTo,
        ...(asinIds && asinIds.length > 0 && { asin_ids: asinIds }),
      }),
    enabled: !!brandId,
    placeholderData: keepPreviousData,
    staleTime: DEFAULT_STALE_TIME,
    select: (data) => {
      if (asinIds !== undefined && asinIds.length === 0) {
        return {
          ...data,
          data: [],
          selectedHeroAsins: 0,
          totalKeywords: 0,
          avgRankDelta: null,
          unitsDeltaPct: null,
          bsrDeltaPct: null,
          correlation: null,
        };
      }
      return data;
    },
  });
};

export const useGetRankTrackerReports = (brandId?: number | null) => {
  return useQuery({
    queryKey: ["rank-tracker", "reports", brandId],
    queryFn: () =>
      apiService.get<RankTrackerReport[], { brand_id: number }>(
        `/rank-tracker/reports`,
        { brand_id: brandId! },
      ),
    enabled: !!brandId,
    placeholderData: keepPreviousData,
    staleTime: DEFAULT_STALE_TIME,
  });
};

export const useExportRankTrackerCsv = () => {
  return useMutation({
    mutationFn: async ({ brandId }: { brandId: number }) => {
      const response = await apiService._api({
        method: 'get',
        url: `/rank-tracker/export`,
        params: { brand_id: brandId },
        responseType: 'blob',
      });

      const blob = new Blob([response as BlobPart], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `rank-tracker-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    onError: (error) => handleApiError(error),
  });
};

export const useGetAllRankTrackerKeywordsForBrand = (brandId?: number | null) => {
  return useQuery({
    queryKey: ["rank-tracker", "all-keywords", brandId],
    queryFn: () =>
      apiService.get<{ heroProductId: number; keywords: RankTrackerKeyword[] }[]>(
        `/rank-tracker/brands/${brandId}/all-keywords`,
      ),
    enabled: !!brandId,
    staleTime: DEFAULT_STALE_TIME,
  });
};

/**
 * GET/PUT /api/rank-tracker/brands/{brandId}/ad-push-cutoffs (AUT-497 slice B, FR-5/FR-6).
 * Keyed on `brandId` so a brand switch re-reads the brand's own cutoffs (FR-12 -- nothing
 * brand-specific is held outside this query). The PUT invalidates this exact key on success.
 */
export const getAdPushCutoffsQueryKey = (brandId?: number | null) =>
  ["rank-tracker", "ad-push-cutoffs", brandId] as const;

/**
 * Reads the brand's ad-push cutoffs. A brand with no custom row returns the defaults 3.5 / 10 / 30
 * with `isCustom: false` (FR-5) -- so a consumer still gets a usable triple before anyone edits.
 * `brandId` is a path segment, never a `brand_id` query param.
 */
export const useGetAdPushCutoffs = (brandId?: number | null) => {
  return useQuery({
    queryKey: getAdPushCutoffsQueryKey(brandId),
    queryFn: () =>
      apiService.get<AdPushCutoffsResponse, undefined>(
        `/rank-tracker/brands/${brandId}/ad-push-cutoffs`,
      ),
    enabled: !!brandId,
    staleTime: DEFAULT_STALE_TIME,
  });
};

/**
 * Saves the brand's ad-push cutoffs (FR-6). Reset-to-defaults is the same PUT with the default
 * triple (FR-7), not a DELETE. The server stamps the actor and timestamp; the body carries only the
 * three cutoffs. On success it invalidates the GET so the column re-reads the committed values.
 */
export const useUpdateAdPushCutoffs = (brandId?: number | null) => {
  return useMutation<AdPushCutoffsResponse, unknown, AdPushCutoffsRequest>({
    mutationFn: (body) =>
      apiService.put<AdPushCutoffsResponse, AdPushCutoffsRequest>(
        `/rank-tracker/brands/${brandId}/ad-push-cutoffs`,
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getAdPushCutoffsQueryKey(brandId) });
    },
    onError: (error) => handleApiError(error),
  });
};

export const useGenerateRankTrackerReport = () => {
  return useMutation({
    mutationFn: ({ brandId }: { brandId: number }) =>
      apiService.post<{ message: string }, { brand_id: number }>(
        `/rank-tracker/reports/generate`,
        { brand_id: brandId },
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["rank-tracker", "reports", variables.brandId],
      });
      toast.success("Report generated successfully.");
    },
    onError: (error) => handleApiError(error),
  });
};
